import { UserOutlined } from '@ant-design/icons';
import React, { FC, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { setHeader, setRoutes } from 'src/core/ui/shell';
import { AppConfig, container } from 'src/inversify.config';
import { UserSummary, UserSummaryStore } from 'src/stores/users-store';
import { CustomFilterField, TableModel, TableView } from 'src/core/ui/collections/table';
import { App, Button, Card, Modal, Row, Tag } from 'antd';
import { ListState, Query } from 'src/core/stores/data-store';
import { formatMessage } from 'src/core/services/http.service';
import NewUserDialog from './user-new';
import { useIdentity } from 'src/core/services/authentication';
import { CommandResult } from 'src/core/stores/types';
import { AutocompleteTextFilter } from 'src/common/components/table-filters/autocomplete-text-filter';
import { ENV } from 'src/';
import { emptyTableModel } from 'src/common/utils';
import { useNavigate, Link } from 'react-router-dom';
import UserSharingDataSettingsModal from './components/user-sharing-data-settings';
import useMediaQuery from 'src/common/hooks/use-media-query';
import { AppConfiguration } from 'src/core/services/configuration.service';
import { OptionsFilterMultiple } from 'src/common/components/table-filters/options-filter-multiple';
import { OrganizationSummaryStore } from 'src/stores/organizations-store';
import CsvReport from '../licenses/reports/csv-report';

const UsersListPage: FC = () => {
  const identity = useIdentity();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const config: AppConfiguration = useContext(AppConfig);
  const [query, setQuery] = useState({
    searchQuery: '',
    orderBy: [{ field: 'email', direction: 'Ascending', useProfile: false }],
    skip: 0,
    take: 10,
  } as Query);

  const usersStore = useMemo(() => container.get(UserSummaryStore), []);
  const usersState = usersStore.state;
  const [newUserVisible, setNewUserVisible] = useState(false);
  const [sharingDataSettingsVisible, setSharingDataSettingsVisible] = useState(false);
  const [exportDialogVisible, setExportDialogVisible] = useState(false);
  const [selectedCsvSeparator, setSelectedCsvSeparator] = useState(',');
  const [isExporting, setIsExporting] = useState(false);
  const onMobile = useMediaQuery('(max-width: 768px)');
  const organizationsStore = container.get(OrganizationSummaryStore);
  const organizationsState = organizationsStore.state;
  const organizationsQuery = {
    searchQuery: '',
    orderBy: [{ field: 'displayName', direction: 'Ascending', useProfile: false }],
    skip: 0,
    take: 10000,
  } as Query;

  const [organizationFilterMode, setOrganizationFilterMode] = useState<'Any' | 'All'>('Any');
  const { modal, notification } = App.useApp();

  setHeader(t('Users'));
  setRoutes([
    { path: '', breadcrumbName: t('Home') },
    { path: 'users', breadcrumbName: t('Users') },
  ]);

  useEffect(
    () => {
      const load = async () => {
        await usersStore.load(query);
        await organizationsStore.load(organizationsQuery);
      };
      load();
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const onSendResetPasswordEmail = async (userId: string) => {
    const response = await usersStore.sendResetPasswordEmail(userId);

    if (response && response?.isSuccess) {
      modal.success({
        title: <h3>{`${t('ResetPasswordEmailSent')}`}</h3>,
      });
    } else {
      modal.error({
        title: <h3>{`${t('ResetPasswordEmailNotSent')}`}</h3>,
        content: config.environment === ENV.DEV ? usersStore.state.errorMessage.value || t('UnexpectedError') : undefined,
      });
    }
  };

  const tableModel = {
    query,
    columns: [
      {
        sortable: true,
        searchable: true,
        field: 'email',
        title: t('Email'),
        applyPartialFilter: true,
        filter: <AutocompleteTextFilter baseUrl={usersStore.baseUrl} fieldName={'Email'} fieldValue={'email'} />,
        fiterField: 'Email',
        partialFilter: true,
        renderer: (data) => (
          <span>
            <Link to={`${data.id}`}>
              <UserOutlined />
              &nbsp;<span>{data.email}</span>
            </Link>
          </span>
        ),
      },
      {
        searchable: true,
        sortable: true,
        sortField: 'DisplayName',
        useProfile: true,
        field: 'DisplayName',
        title: t('DisplayName'),
        renderer: (data) => <span>{data.displayName}</span>,
      },
      {
        queryFilterMode: CustomFilterField.SearchInCollection,
        field: 'Organizations/OrganizationId',
        title: t('Organization'),
        multipleFilterMode: organizationFilterMode,
        filter: (
          <OptionsFilterMultiple
            allowChangeFilterMode
            changeFilterMode={setOrganizationFilterMode}
            options={organizationsState.value.items.map((v) => ({ value: v.id, label: v.displayName }))}
          />
        ),
        renderer: (data) => (
          <span>
            {data.organizations.map((org) => (
              <Tag key={org.organizationDisplayName} style={{ marginTop: 6 }}>
                {org.organizationDisplayName}
              </Tag>
            ))}
          </span>
        ),
      },
      {
        title: '',
        renderer: (data: UserSummary) => (
          <span className="clickable-span strong" onClick={() => onSendResetPasswordEmail(data.id)}>
            {t('SendResetPasswordEmail')}
          </span>
        ),
      },
      {
        title: '',
        renderer: (data: UserSummary) => (
          <span className="clickable-span strong" onClick={() => onResetLockout(data.id)}>
            {t('ResetLockout')}
          </span>
        ),
      },
    ],
    sortFields: [],
    data: usersState.value as ListState<UserSummary>,
  } as TableModel<UserSummary>;

  const onResetLockout = async (userId: string) => {
    const response = await usersStore.resetLockout(userId);
    if (response && response?.isSuccess) {
      notification.success({
        message: t('LockoutReset'),
        placement: 'bottomLeft',
      });
    } else {
      notification.error({
        message:
          config.environment === ENV.DEV
            ? `${t('LockoutNotReset')}:  ${usersStore.state.errorMessage.value || t('UnexpectedError')}`
            : t('LockoutNotResetError'),
        placement: 'bottomLeft',
      });
    }
  };

  const onDeleteUser = async (item: UserSummary) => {
    if (item.id === identity.profile['sub']) {
      modal.error({
        title: t('CanNotDeleteOwnUser'),
        okText: t('OK'),
        cancelText: t('Cancel'),
      });
      return Promise.resolve<CommandResult<any>>({ isSuccess: false, error: t("Can't delete own user") } as CommandResult<any>);
    }
    const response = await usersStore.delete(item.id);
    if (!response.isSuccess && response.error) {
      modal.error({
        title: t('SomethingWentWrong'),
        content: config.environment === ENV.DEV ? response.error : t('ErrorDeletingUser'),
        okText: t('OK'),
        cancelText: t('Cancel'),
      });
    }
    return response;
  };

  return (
    <Row align="top" justify="space-between">
      <Card className="card-table table-view-mobile-horizontal-toolbar" style={{ width: '100%' }}>
        <TableView
          rowKey="id"
          canCreateNew={false}
          onNewItem={() => setNewUserVisible(true)}
          newItemName={t('AddUser')}
          canDelete
          onDeleteRow={onDeleteUser}
          onQueryChanged={(query: Query) => {
            setQuery(query);
            usersStore.load(query);
          }}
          onRefresh={() => usersStore.load(query)}
          model={usersState.errorMessage.value ? emptyTableModel : tableModel}
          error={
            usersState.errorMessage.value && (config.environment === ENV.DEV ? formatMessage(usersState.errorMessage.value) : t('UsersTableError'))
          }
          rightToolbar={
            <>
              <li>
                <Button
                  style={{ width: onMobile ? '170px' : 'auto' }}
                  type="primary"
                  onClick={() => {
                    setExportDialogVisible(true);
                  }}
                >
                  {t('ExportCSV')}
                </Button>
              </li>
            </>
          }
        />
        {newUserVisible && (
          <NewUserDialog
            onClose={(response: any) => {
              setNewUserVisible(false);
              if (response) {
                navigate(`${response.identity}`);
              }
            }}
          />
        )}
        {sharingDataSettingsVisible && <UserSharingDataSettingsModal onCloseModal={() => setSharingDataSettingsVisible(false)} />}

        <Modal
          title={<h3>{`${t('Reports')}`}</h3>}
          okText={t('Export')}
          cancelText={t('Cancel')}
          cancelButtonProps={{ type: 'default' }}
          open={exportDialogVisible}
          okButtonProps={{ loading: isExporting }}
          onCancel={() => {
            setExportDialogVisible(false);
          }}
          onOk={async () => {
            setIsExporting(true);
            const csvQuery = { ...query, take: 100000, skip: 0 };
            await usersStore.exportCsv(csvQuery, selectedCsvSeparator, 'users-report.csv');
            setIsExporting(false);
            setExportDialogVisible(false);
          }}
        >
          <CsvReport
            onChange={(item) => {
              setSelectedCsvSeparator(item);
            }}
          />
        </Modal>
      </Card>
    </Row>
  );
};

export default UsersListPage;
